import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import { RecommendationCard } from "../components/home/recommendation-card";
import { RecentWorkouts } from "../components/home/recent-workouts";
import { UploadCard } from "../components/home/upload-card";
import { QuickActions } from "../components/home/quick-actions";
import { LogWorkoutModal } from "../components/home/log-workout-modal";
import { WeeklyStatsSection } from "../components/home/weekly-stats";
import { getWeeklyStats, WeeklyStats, getCompletedWorkouts } from "../services/workouts.service";
import { getNextBestWorkout } from "../services/home.service";
import { getProfile } from "../services/profile.service";
import { StrongifierCompletedWorkoutV2 } from "src/models/strongifier-completed-workout-v2";
import { NextBestWorkout } from "../models/next-best-workout";

/** "Good morning" / "Good afternoon" / "Good evening" by local time */
const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

/** Start of the current week (Monday, local time) */
const getWeekStart = (): Date => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = (start.getDay() + 6) % 7; // Monday = 0
  start.setDate(start.getDate() - day);
  return start;
};

interface WeekStats {
  count: number;
  distanceKm: number;
  durationMinutes: number;
}

const getWeekStats = (activities: StrongifierCompletedWorkoutV2[]): WeekStats => {
  const weekStart = getWeekStart();
  const thisWeek = activities.filter((a) => {
    const d = new Date(a.local_activity_datetime || a.activity_datetime);
    return !isNaN(d.getTime()) && d >= weekStart;
  });
  return {
    count: thisWeek.length,
    distanceKm: thisWeek.reduce((sum, a) => sum + (a.distance_km || 0), 0),
    durationMinutes: thisWeek.reduce((sum, a) => sum + (a.duration_minutes || 0), 0),
  };
};

/** Minutes → "4h 48m" */
const formatHoursMinutes = (minutes: number): string => {
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const HomePage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<StrongifierCompletedWorkoutV2[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [recommendation, setRecommendation] = useState<NextBestWorkout | null>(null);
  const [recommendationLoading, setRecommendationLoading] = useState(true);
  const [preferredName, setPreferredName] = useState<string | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null);
  const [weeklyStatsLoading, setWeeklyStatsLoading] = useState(true);

  const fetchWeeklyStats = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await getWeeklyStats(token, timezone);
    setWeeklyStats(data ?? null);
    setWeeklyStatsLoading(false);
  }, [getAccessTokenSilently]);

  const fetchActivities = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const { data } = await getCompletedWorkouts(token);
    setActivities(data);
    setActivitiesLoading(false);
  }, [getAccessTokenSilently]);

  const fetchRecommendation = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await getNextBestWorkout(token, timezone);
    setRecommendation(data ?? null);
    setRecommendationLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchActivities();
    fetchRecommendation();
    fetchWeeklyStats();

    const fetchName = async () => {
      const token = await getAccessTokenSilently();
      const { data } = await getProfile(token);
      if (data?.preferred_name) {
        setPreferredName(data.preferred_name);
      }
    };
    fetchName();
  }, [fetchActivities, fetchRecommendation, fetchWeeklyStats, getAccessTokenSilently]);

  const handleUploaded = () => {
    fetchActivities();
    fetchRecommendation();
    fetchWeeklyStats();
  };

  const handleLogged = (workout: StrongifierCompletedWorkoutV2) => {
    setLogOpen(false);
    const minutes = Math.round(workout.duration_minutes);
    const durationLabel =
      minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
    setToast(`${workout.workout_name ?? workout.activity_type} · ${durationLabel} logged`);
    window.setTimeout(() => setToast(null), 3200);
    fetchActivities();
    fetchRecommendation();
    fetchWeeklyStats();
  };

  const stats = getWeekStats(activities);
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <PageLayout>
      <>
      <div className="home__page">
        <main className="home__main">
          <div className="home__header">
            <div>
              <h1 className="home__greeting">
                {getGreeting()}
                {preferredName ? `, ${preferredName}` : ""}
              </h1>
              <div className="home__date">
                {today} — here's where your training stands.
              </div>
            </div>
            <div className="home__stats">
              <div className="home__stat">
                <div className="home__stat-value">{stats.count}</div>
                <div className="home__stat-label">Workouts this week</div>
              </div>
              <div className="home__stat-divider"></div>
              <div className="home__stat">
                <div className="home__stat-value">
                  {stats.distanceKm.toFixed(1)} km
                </div>
                <div className="home__stat-label">Distance</div>
              </div>
              <div className="home__stat-divider"></div>
              <div className="home__stat">
                <div className="home__stat-value">
                  {formatHoursMinutes(stats.durationMinutes)}
                </div>
                <div className="home__stat-label">Time</div>
              </div>
            </div>
          </div>
          <RecommendationCard
            recommendation={recommendation}
            loading={recommendationLoading}
          />
          <WeeklyStatsSection stats={weeklyStats} loading={weeklyStatsLoading} />
          <div className="home__grid">
            <RecentWorkouts
              workouts={activities}
              loading={activitiesLoading}
              onOpen={(id) => navigate(`/workouts/${id}`)}
            />
            <div className="home__side">
              <UploadCard onUploaded={handleUploaded} />
              <QuickActions onLogWorkout={() => setLogOpen(true)} />
            </div>
          </div>
        </main>
      </div>
      {logOpen && (
        <LogWorkoutModal onClose={() => setLogOpen(false)} onLogged={handleLogged} />
      )}
      {toast && (
        <div
          className="fixed z-[60] flex items-center gap-[9px] bg-[#1a2130] rounded-[9px] px-[18px] py-[11px]"
          style={{
            top: "88px",
            left: "50%",
            transform: "translateX(-50%)",
            border: "1px solid rgba(62,201,167,.45)",
            boxShadow: "0 12px 32px rgba(0,0,0,.45)",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3ec9a7" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"></path>
          </svg>
          <span className="text-[13.5px] font-semibold text-[#dfe5f0]">{toast}</span>
        </div>
      )}
      </>
    </PageLayout>
  );
};
